import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatDay, parseIsoDay } from '@wodi/domain';
import type { CodeSystemVersionView, CodeSystemView, TerminologyCoding } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { activateVersion, codeSystemsQuery, codeSystemVersionsQuery } from '../api/terminology.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { TerminologyPicker } from '../terminology/terminology-picker.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Terminology (plan §6.2): which code systems and versions are in use, their
// licence notices, and a search to try them. Only the reference data publisher can change
// the active version of shared reference data (ADR 0009).

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const STATUS_LABELS: Record<CodeSystemVersionView['status'], string> = {
  importing: 'Loading',
  ready: 'Ready',
  active: 'Active',
  retired: 'Retired',
  failed: 'Failed',
};

function statusTag(status: CodeSystemVersionView['status']) {
  const state =
    status === 'active'
      ? 'active'
      : status === 'retired'
        ? 'neutral'
        : status === 'failed'
          ? 'failed'
          : 'pending';
  return <Tag state={state}>{STATUS_LABELS[status]}</Tag>;
}

/** A release date as `22 Sep 2026`. */
function day(iso: string | null): string {
  if (iso === null) return '';
  const parsed = parseIsoDay(iso);
  return parsed.ok ? formatDay(parsed.value) : iso;
}

function ActivateDialog({
  system,
  version,
  onClose,
}: {
  system: CodeSystemView;
  version: CodeSystemVersionView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const activate = useMutation({
    mutationFn: (reason: string) => activateVersion(system.key, version.id, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['terminology'] });
      toasts.show({
        title: 'Version made active',
        description: `${system.name} ${version.version}`,
      });
      onClose();
    },
  });
  return (
    <ReasonDialog
      title={`Make ${version.version} active?`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      confirmLabel="Make active"
      pendingLabel="Making active…"
      isPending={activate.isPending}
      error={
        activate.error === null
          ? null
          : message(activate.error, "The version wasn't made active. Try again.")
      }
      onConfirm={(reason) => {
        activate.mutate(reason);
      }}
    >
      <p>
        {system.owner === 'reference'
          ? `Every organisation's ${system.name} pickers will offer codes from ${version.version} from now on. Codes already recorded keep the version they were recorded with.`
          : `Pickers will offer codes from ${version.version} from now on. Codes already recorded keep their version.`}
      </p>
    </ReasonDialog>
  );
}

function Versions({ system, canManage }: { system: CodeSystemView; canManage: boolean }) {
  const online = useOnline();
  const versions = useQuery(codeSystemVersionsQuery(system.key));
  const [activating, setActivating] = useState<CodeSystemVersionView | null>(null);
  if (versions.isPending) return <LoadingBlock label="Loading versions" rows={2} />;
  if (versions.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void versions.refetch()}>
            Try again
          </Button>
        }
      >
        {message(versions.error, "Versions couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  return (
    <>
      <Table aria-label={`${system.name} versions`}>
        <TableHeader>
          <Column isRowHeader>Version</Column>
          <Column>Released</Column>
          <Column>Codes</Column>
          <Column>Status</Column>
          <Column>Actions</Column>
        </TableHeader>
        <TableBody items={versions.data.items} renderEmptyState={() => 'No versions loaded yet.'}>
          {(v) => (
            <Row id={v.id}>
              <Cell>
                <span className="wodi-id">{v.version}</span>
              </Cell>
              <Cell>{day(v.releaseDate)}</Cell>
              <Cell>{v.conceptCount.toLocaleString('en-KE')}</Cell>
              <Cell>{statusTag(v.status)}</Cell>
              <Cell>
                {canManage && (v.status === 'ready' || v.status === 'retired') ? (
                  <Button
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setActivating(v);
                    }}
                  >
                    Make active
                  </Button>
                ) : null}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {activating === null ? null : (
        <ActivateDialog
          system={system}
          version={activating}
          onClose={() => {
            setActivating(null);
          }}
        />
      )}
    </>
  );
}

export function TerminologyPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'terminology.read');
  const systems = useQuery({ ...codeSystemsQuery, enabled: allowed });
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<TerminologyCoding | null>(null);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Terminology" />
        <Notice tone="info">
          You don't have permission to see code systems. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }

  const items = systems.data?.items ?? [];
  const publisher = systems.data?.isReferencePublisher ?? false;
  const canManage = can(session, 'terminology.manage');
  const selected = items.find((s) => s.key === open) ?? null;
  const searchable = items.filter((s) => s.activeVersion !== null);

  return (
    <SettingsPage title="Terminology">
      <p>
        The code systems Wodi uses for diagnoses, lab tests and services. National code systems are
        shared by every organisation and kept up to date by the Wodi reference data team.
      </p>
      {systems.isPending ? <LoadingBlock label="Loading code systems" rows={3} /> : null}
      {systems.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void systems.refetch()}>
              Try again
            </Button>
          }
        >
          {message(systems.error, "Code systems couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {systems.isSuccess ? (
        <Table aria-label="Code systems">
          <TableHeader>
            <Column isRowHeader>Code system</Column>
            <Column>Active version</Column>
            <Column>Released</Column>
            <Column>Codes</Column>
            <Column>Status</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody items={items} renderEmptyState={() => 'No code systems yet.'}>
            {(s) => (
              <Row id={s.id}>
                <Cell>{s.name}</Cell>
                <Cell>
                  {s.activeVersion === null ? (
                    ''
                  ) : (
                    <span className="wodi-id">{s.activeVersion.version}</span>
                  )}
                </Cell>
                <Cell>{day(s.activeVersion?.releaseDate ?? null)}</Cell>
                <Cell>{s.activeVersion?.conceptCount.toLocaleString('en-KE') ?? ''}</Cell>
                <Cell>
                  <Tag state={s.activeVersion === null ? 'pending' : 'active'}>
                    {s.activeVersion === null ? 'Not set up' : 'Active'}
                  </Tag>
                </Cell>
                <Cell>
                  <Button
                    dense
                    variant="quiet"
                    aria-label={`Versions of ${s.name}`}
                    onPress={() => {
                      setOpen(open === s.key ? null : s.key);
                    }}
                  >
                    {open === s.key ? 'Hide versions' : 'Versions'}
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {selected === null ? null : (
        <section aria-label={`${selected.name} details`}>
          <h2>{selected.name}</h2>
          <p>{`Published by ${selected.publisher}.`}</p>
          <p>{selected.licenceNotice}</p>
          <Versions
            system={selected}
            canManage={canManage && (selected.owner === 'tenant' || publisher)}
          />
        </section>
      )}
      {searchable.length === 0 ? null : (
        <section aria-label="Try a search">
          <h2>Try a search</h2>
          <TerminologyPicker
            label="Search codes"
            description="Search by name or code, as a clinician would. Nothing is recorded."
            systems={searchable.map((s) => s.key)}
            onSelect={setPicked}
          />
          {picked === null ? null : (
            <p>
              <span className="wodi-id">{picked.code}</span> {picked.display}
              {` (version ${picked.version}${picked.source === 'national_service' ? ', from the national terminology service' : ''})`}
            </p>
          )}
        </section>
      )}
    </SettingsPage>
  );
}
